// Global Instructions Rule Applied!

import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../state_manager/hooks/useAppDispatch'
import { useDrawer } from '../../../core/contexts/DrawerContext.jsx'
import { toggleTaskCompleted, updateTask } from '../../../store/slices/tasksSlice'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { AccountRecord } from '../../../state_manager/data_models/account_record.ts'
import { TaskRecordFields } from '../../../state_manager/data_models/task_record.ts'
import { fetchCompanyById } from '../services/companiesService.ts'
import { selectCompanyById } from '../../../state_manager/state_update_actions/companies/selectors.ts'
import { selectActiveRecordById } from '../../../state_manager/state_update_actions/active_records/selectors.ts'
import {
  setActiveRecord,
  registerRecordUsage
} from '../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'

/**
 * Custom hook for managing drawer state and operations for companies
 * Handles drawer opening, closing, and form submission logic
 */
export const useCompaniesDrawer = (companies: AccountRecord[]) => {
  const {
    isOpen: isDrawerOpen,
    recordId,
    isNew,
    openDrawerForNew,
    openDrawerForRecord,
    closeDrawer,
    formType
  } = useDrawer()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  // Drawer-specific state
  const [selectedCompany, setSelectedCompany] = useState<AccountRecord | null>(null)
  const hasAttemptedFetch = useRef(false)
  const companiesState = useSelector((state: any) => state.companies)

  // Drawer handlers
  const handleOpenDrawerForNew = useCallback(() => {
    setSelectedCompany(null)
    openDrawerForNew()
  }, [openDrawerForNew])

  const handleOpenDrawerForCompany = useCallback(
    (companyId: string | number) => {
      const company = companies.find((c) => c[AccountRecordFields.accounts__id]?.toString() === companyId.toString())
      setSelectedCompany(company || null)
      openDrawerForRecord(companyId)
    },
    [companies, openDrawerForRecord]
  )

  const handleCompanyNameClick = useCallback(
    (key: string, value: any, companyId: string | number) => {
      if (companyId) {
        handleOpenDrawerForCompany(companyId)
      }
    },
    [handleOpenDrawerForCompany]
  )

  // Handle tasks click to open drawer with tasks focus
  const handleTasksClick = useCallback(
    (tasks: any[], companyRecord: AccountRecord, taskId: string | null = null) => {
      // Extract company ID from the company record passed from the table
      const companyId = companyRecord?.[AccountRecordFields.accounts__id]

      if (companyId) {
        // Build query parameters
        const params = new URLSearchParams()

        // If specific task ID is provided, add it to open that task's detail panel
        if (taskId) {
          params.set('taskId', taskId)
        }

        // Open drawer with or without query parameters
        const queryString = params.toString()
        navigate(`/companies/forms/drawer/${companyId}${queryString ? `?${queryString}` : ''}`)
      }
    },
    [navigate]
  )

  // Handle task completion toggle
  const handleTaskToggle = useCallback(
    async (taskId: string, companyId: string, completed: boolean) => {
      try {
        // Update tasks slice immediately for instant feedback
        dispatch(toggleTaskCompleted({ taskId, dealId: companyId }))

        // Update backend - optimistic updates removed
        await dispatch(
          updateTask({
            dealId: companyId,
            taskId,
            taskData: {
              [TaskRecordFields.tasks__id]: taskId,
              [TaskRecordFields.tasks__completed]: completed
            },
            recordType: 'company'
          })
        ).unwrap()
      } catch (error) {
        // If backend update fails, revert task slice
        dispatch(toggleTaskCompleted({ taskId, dealId: companyId }))
        console.error('Failed to update task:', error)
      }
    },
    [dispatch]
  )

  const handleDrawerCancel = useCallback(() => {
    setSelectedCompany(null)
    closeDrawer()
  }, [closeDrawer])

  // Reset fetch attempt flag when recordId changes
  useEffect(() => {
    hasAttemptedFetch.current = false
  }, [recordId])

  // Get company from activeRecords first
  const companyFromActiveRecords = useSelector((state: any) =>
    recordId && !isNew ? selectActiveRecordById(state, recordId) : null
  )

  // Find company when recordId changes (for editing existing companies)
  // Check activeRecords first, then companies array, then module state, then fetch from API
  useEffect(() => {
    // Only proceed if we're actually on the companies route
    // Check both formType from drawer context and location pathname
    const isCompaniesRoute = formType === 'company' || location.pathname.startsWith('/companies')

    if (!isCompaniesRoute) {
      // Not on companies route, don't fetch
      return
    }

    const currentToken = getAccessToken(store.getState().auth)
    if (recordId && !isNew && currentToken) {
      // First check activeRecords
      if (companyFromActiveRecords) {
        setSelectedCompany(companyFromActiveRecords)
        hasAttemptedFetch.current = true
        return
      }

      // Then try to find in companies array
      const company = companies.find((c) => c[AccountRecordFields.accounts__id]?.toString() === recordId.toString())

      if (company) {
        setSelectedCompany(company)
        hasAttemptedFetch.current = true
      } else if (!hasAttemptedFetch.current) {
        // Company not found in array, try to fetch from Redux state
        const companyFromState = selectCompanyById({ companies: companiesState }, recordId)

        if (companyFromState) {
          setSelectedCompany(companyFromState)
          hasAttemptedFetch.current = true
        } else {
          // Not in state either, fetch from API
          hasAttemptedFetch.current = true
          fetchCompanyById(recordId, currentToken)
            .then((response) => {
              if (response.data && !response.error) {
                const fetchedCompany = response.data as AccountRecord
                // Only add to activeRecords, not module state
                const companyIdString = recordId.toString()
                dispatch(setActiveRecord({ uuid: companyIdString, record: fetchedCompany }))
                dispatch(registerRecordUsage({ uuid: companyIdString, context: 'drawer' }))
                setSelectedCompany(fetchedCompany)
              } else if (response.error) {
                console.error('Error fetching company:', response.error)
                setSelectedCompany(null)
              }
            })
            .catch((error) => {
              console.error('Error fetching company:', error)
              setSelectedCompany(null)
            })
        }
      }
    } else if (isNew || !recordId) {
      setSelectedCompany(null)
      hasAttemptedFetch.current = false
    }
  }, [recordId, isNew, companies, dispatch, companiesState, companyFromActiveRecords, formType, location.pathname])

  return {
    // Drawer state
    isDrawerOpen,
    recordId,
    isNew,
    selectedCompany,

    // Handlers
    handleOpenDrawerForNew,
    handleOpenDrawerForCompany,
    handleCompanyNameClick,
    handleTasksClick,
    handleTaskToggle,
    handleDrawerCancel
  }
}
